(() => {
  'use strict';


  const MAPLIBRE_VERSION = '5.6.1';

  const mapStyles = {
    dark: 'https://tiles.openfreemap.org/styles/dark',
    positron: 'https://tiles.openfreemap.org/styles/positron',
    liberty: 'https://tiles.openfreemap.org/styles/liberty'
  };

  let mapLibrePromise = null;

  const ensureStylesheet = (href) => {
    if (document.querySelector(`link[href="${href}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.dataset.radarMaplibre = 'true';
    document.head.appendChild(link);
  };

  const loadScript = (src) => new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing) {
      if (window.maplibregl) {
        resolve(window.maplibregl);
        return;
      }

      existing.addEventListener(
        'load',
        () => resolve(window.maplibregl),
        { once: true }
      );
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve(window.maplibregl);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  const loadMapLibre = () => {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    if (mapLibrePromise) return mapLibrePromise;

    ensureStylesheet(
      `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.css`
    );

    const scripts = [
      `https://cdn.jsdelivr.net/npm/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`,
      `https://unpkg.com/maplibre-gl@${MAPLIBRE_VERSION}/dist/maplibre-gl.js`
    ];

    mapLibrePromise = scripts.reduce(
      (promise, src) => promise.catch(() => loadScript(src)),
      Promise.reject(new Error('Inicializando MapLibre'))
    ).then((library) => {
      if (!library) throw new Error('MapLibre no está disponible.');
      return library;
    });

    return mapLibrePromise;
  };

  const initializeCollectionMap = async (section) => {
    const canvas = section.querySelector(
      '[data-radar-collection-map-canvas]'
    );
    const dataElement = section.querySelector(
      '[data-radar-collection-map-data]'
    );
    const loader = section.querySelector(
      '[data-radar-collection-map-loader]'
    );

    if (!canvas || !dataElement) return;

    let payload = [];

    try {
      payload = JSON.parse(dataElement.textContent || '[]');
    } catch (error) {
      if (loader) loader.hidden = true;
      return;
    }

    const cities = new Map();

    payload.forEach((entry) => {
      const data = entry.data;

      if (!data || !Array.isArray(data.cities)) return;

      data.cities.forEach((city) => {
        const latitude = Number(city.latitude);
        const longitude = Number(city.longitude);

        if (
          !Number.isFinite(latitude) ||
          !Number.isFinite(longitude)
        ) {
          return;
        }

        const key = [
          String(city.countryCode || city.country || '')
            .trim()
            .toLowerCase(),
          normalize(city.name)
        ].join('|');

        const current = cities.get(key) || {
          latitude,
          longitude,
          registrations: 0
        };

        current.registrations += Number(city.registrations) || 1;
        cities.set(key, current);
      });
    });

    try {
      const maplibregl = await loadMapLibre();

      const map = new maplibregl.Map({
        container: canvas,
        style: mapStyles[section.dataset.mapStyle] || mapStyles.dark,
        center: [-20, 12],
        zoom: 1.25,
        minZoom: 1,
        maxZoom: 6,
        interactive: false,
        attributionControl: true
      });

      map.on('load', () => {
        if (loader) loader.hidden = true;

        const points = Array.from(cities.values());
        const bounds = new maplibregl.LngLatBounds();

        points.slice(0, 40).forEach((city, index) => {
          const signal = document.createElement('span');
          signal.className = 'radar-collection__live-signal';
          signal.style.setProperty(
            '--signal-size',
            `${Math.max(
              12,
              Math.min(
                28,
                12 + Math.log10(city.registrations + 1) * 7
              )
            )}px`
          );
          signal.style.animationDelay = `${(index % 8) * .2}s`;

          new maplibregl.Marker({
            element: signal,
            anchor: 'center'
          })
            .setLngLat([city.longitude, city.latitude])
            .addTo(map);

          bounds.extend([city.longitude, city.latitude]);
        });

        if (points.length === 1) {
          map.jumpTo({
            center: [points[0].longitude, points[0].latitude],
            zoom: 3.2
          });
        } else if (points.length > 1) {
          map.fitBounds(bounds, {
            padding: window.innerWidth < 750 ? 38 : 90,
            maxZoom: 3.8,
            duration: 0
          });
        }

        window.setTimeout(() => map.resize(), 100);
      });

      window.addEventListener(
        'resize',
        () => map.resize(),
        { passive: true }
      );
    } catch (error) {
      if (loader) loader.hidden = true;
    }
  };

  const sections = document.querySelectorAll('[data-radar-collection]');
  if (!sections.length) return;

  const normalize = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[áàäâãå]/g, 'a')
    .replace(/[éèëê]/g, 'e')
    .replace(/[íìïî]/g, 'i')
    .replace(/[óòöôõ]/g, 'o')
    .replace(/[úùüû]/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/ç/g, 'c')
    .replace(/\s+/g, ' ');

  sections.forEach((section) => {
    initializeCollectionMap(section);

    const grid = section.querySelector('[data-radar-grid]');
    if (!grid) return;

    let cards = Array.from(grid.querySelectorAll('[data-radar-card]'));

    const search = section.querySelector('[data-radar-search]');
    const sort = section.querySelector('[data-radar-sort]');
    const filtersHost = section.querySelector(
      '[data-radar-dynamic-filters]'
    );
    const smartViews = section.querySelector('[data-radar-smart-views]');
    const featuredHost = section.querySelector('[data-radar-featured]');
    const noResults = section.querySelector('[data-radar-no-results]');
    const infinite = section.querySelector('[data-radar-infinite]');
    const sentinel = section.querySelector(
      '[data-radar-infinite-sentinel]'
    );
    const loader = section.querySelector('[data-radar-infinite-loader]');
    const infiniteStatus = section.querySelector(
      '[data-radar-infinite-status]'
    );
    const loadMoreButton = section.querySelector(
      '[data-radar-load-more]'
    );

    const homeCountry = normalize(section.dataset.homeCountry);
    const newDays = Math.max(1, Number(section.dataset.newDays) || 30);
    const featuredCount = Math.max(
      1,
      Number(section.dataset.featuredCount) || 5
    );

    let activeFilter = 'all';
    let activeView = section.dataset.defaultView || 'trending';
    let nextUrl = infinite?.dataset.nextUrl || '';
    let loadingNext = false;
    let loadedPage = Number(infinite?.dataset.currentPage || 1);
    const totalPages = Number(infinite?.dataset.totalPages || 1);

    const rebuildFilters = () => {
      if (!filtersHost) return;

      const values = new Map();

      cards.forEach((card) => {
        const type = String(card.dataset.type || '').trim();
        const origin = String(card.dataset.origin || '').trim();

        if (type) {
          values.set(`type:${normalize(type)}`, {
            type: 'type',
            value: normalize(type),
            label: type
          });
        }

        if (origin) {
          values.set(`origin:${normalize(origin)}`, {
            type: 'origin',
            value: normalize(origin),
            label: origin
          });
        }
      });

      filtersHost.innerHTML = '';

      Array.from(values.values())
        .sort((a, b) => a.label.localeCompare(b.label, 'es'))
        .forEach((filter) => {
          const button = document.createElement('button');
          const key = `${filter.type}:${filter.value}`;

          button.type = 'button';
          button.textContent = filter.label;
          button.dataset.radarFilter = filter.value;
          button.dataset.radarFilterType = filter.type;
          button.classList.toggle('is-active', activeFilter === key);
          filtersHost.appendChild(button);
        });
    };

    const compareCards = (mode) => (a, b) => {
      if (mode === 'title-asc') {
        return a.dataset.title.localeCompare(b.dataset.title, 'es');
      }

      if (mode === 'title-desc') {
        return b.dataset.title.localeCompare(a.dataset.title, 'es');
      }

      if (mode === 'heat-desc') {
        return Number(b.dataset.heat) - Number(a.dataset.heat);
      }

      if (mode === 'heat-asc') {
        return Number(a.dataset.heat) - Number(b.dataset.heat);
      }

      if (mode === 'newest') {
        return Number(b.dataset.created) - Number(a.dataset.created);
      }

      return (
        Number(b.dataset.registrations) -
          Number(a.dataset.registrations) ||
        Number(b.dataset.heat) -
          Number(a.dataset.heat) ||
        Number(a.dataset.index) -
          Number(b.dataset.index)
      );
    };

    const sortCards = (mode = 'relevance') => {
      cards
        .slice()
        .sort(compareCards(mode))
        .forEach((card) => grid.appendChild(card));
    };

    const matchesView = (card) => {
      if (activeView === 'all') return true;

      if (activeView === 'trending') {
        return true;
      }

      if (activeView === 'new') {
        const created = Number(card.dataset.created) * 1000;

        return (
          created > 0 &&
          Date.now() - created <= newDays * 86400000
        );
      }

      if (activeView === 'international') {
        const origin = normalize(card.dataset.origin);
        return Boolean(origin && homeCountry && origin !== homeCountry);
      }

      return true;
    };

    const applyFilters = () => {
      const query = normalize(search?.value);
      let visibleCount = 0;

      cards.forEach((card) => {
        const title = normalize(card.dataset.title);
        const type = normalize(card.dataset.type);
        const origin = normalize(card.dataset.origin);

        const matchesSearch =
          !query ||
          title.includes(query) ||
          type.includes(query) ||
          origin.includes(query);

        let matchesDynamicFilter = true;

        if (activeFilter !== 'all') {
          const [filterType, filterValue] = activeFilter.split(':');

          matchesDynamicFilter =
            filterType === 'type'
              ? type === filterValue
              : origin === filterValue;
        }

        const visible =
          matchesSearch &&
          matchesDynamicFilter &&
          matchesView(card);

        card.hidden = !visible;

        if (visible) visibleCount += 1;
      });

      if (noResults) {
        noResults.hidden =
          visibleCount > 0 ||
          Boolean(nextUrl) ||
          loadingNext;
      }

      if (
        visibleCount === 0 &&
        nextUrl &&
        !loadingNext &&
        (
          query ||
          activeFilter !== 'all' ||
          activeView !== 'all'
        )
      ) {
        window.setTimeout(loadNextPage, 90);
      }

      return visibleCount;
    };

    const refreshCardBadges = () => {
      const ranked = cards
        .slice()
        .sort(compareCards('relevance'));

      ranked.forEach((card, index) => {
        const badge = card.querySelector(
          '[data-radar-card-badge]'
        );

        if (!badge) return;

        const created = Number(card.dataset.created) * 1000;
        const isNew =
          created > 0 &&
          Date.now() - created <= newDays * 86400000;

        let label = '';

        if (
          index === 0 &&
          Number(card.dataset.registrations) > 0
        ) {
          label = 'Más apoyado';
        } else if (isNew) {
          label = 'Nuevo';
        } else if (index < Math.min(6, ranked.length)) {
          label = 'Tendencia';
        }

        badge.textContent = label;
        badge.hidden = !label;
      });
    };

    const createFeaturedCard = (card, index) => {
      const link = document.createElement('a');
      const media = document.createElement('span');
      const copy = document.createElement('span');
      const rank = document.createElement('i');
      const kicker = document.createElement('small');
      const title = document.createElement('strong');
      const meta = document.createElement('em');

      link.className = 'radar-collection__featured-card';
      link.href = card.dataset.url || '#';

      media.className = 'radar-collection__featured-media';

      if (card.dataset.image) {
        const image = document.createElement('img');
        image.src = card.dataset.image;
        image.alt = card.dataset.title || '';
        image.loading = 'lazy';
        media.appendChild(image);
      }

      rank.textContent = String(index + 1).padStart(2, '0');
      media.appendChild(rank);

      copy.className = 'radar-collection__featured-copy';
      kicker.textContent = index === 0 ? 'Más apoyado' : 'Tendencia';
      title.textContent = card.dataset.title || 'Artista';
      meta.textContent = [
        card.dataset.type,
        card.dataset.origin
      ].filter(Boolean).join(' · ');

      copy.append(kicker, title, meta);
      link.append(media, copy);

      return link;
    };

    const refreshFeatured = () => {
      if (!featuredHost) return;

      featuredHost.innerHTML = '';
      refreshCardBadges();

      cards
        .slice()
        .sort(compareCards('relevance'))
        .slice(0, featuredCount)
        .forEach((card, index) => {
          featuredHost.appendChild(createFeaturedCard(card, index));
        });
    };

    const updateInfiniteState = ({
      loading = false,
      error = false
    } = {}) => {
      if (!infinite) return;

      grid.setAttribute('aria-busy', loading ? 'true' : 'false');

      if (loader) loader.hidden = !loading;

      if (loadMoreButton) {
        loadMoreButton.hidden = !error;
        loadMoreButton.textContent = error
          ? 'Reintentar carga'
          : 'Cargar más artistas';
      }

      if (!infiniteStatus) return;

      if (error) {
        infiniteStatus.textContent =
          'No pudimos cargar más artistas.';
      } else if (loading) {
        infiniteStatus.textContent =
          `Cargando ${Math.min(loadedPage + 1, totalPages)} de ${totalPages}…`;
      } else if (!nextUrl) {
        infiniteStatus.textContent =
          'Llegaste al final de RADAR.';
      } else {
        infiniteStatus.textContent =
          'Sigue bajando para descubrir más artistas.';
      }
    };

    async function loadNextPage() {
      if (!nextUrl || loadingNext) return;

      loadingNext = true;
      updateInfiniteState({ loading: true });

      try {
        const requestUrl = new URL(nextUrl, window.location.origin);
        const response = await fetch(requestUrl.toString(), {
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const nextSection = parsed.querySelector('[data-radar-collection]');
        const nextGrid = nextSection?.querySelector('[data-radar-grid]');
        const nextInfinite = nextSection?.querySelector(
          '[data-radar-infinite]'
        );

        if (!nextGrid) {
          throw new Error('No se encontró la siguiente grilla.');
        }

        const knownUrls = new Set(
          cards.map((card) => card.dataset.url).filter(Boolean)
        );

        const incoming = Array.from(
          nextGrid.querySelectorAll('[data-radar-card]')
        )
          .map((card) => document.importNode(card, true))
          .filter((card) => {
            const url = card.dataset.url;
            if (!url || knownUrls.has(url)) return false;
            knownUrls.add(url);
            return true;
          });

        incoming.forEach((card) => {
          card.dataset.index = String(cards.length);
          cards.push(card);
          grid.appendChild(card);
        });

        loadedPage += 1;
        nextUrl = nextInfinite?.dataset.nextUrl || '';

        if (infinite) {
          infinite.dataset.nextUrl = nextUrl;
          infinite.dataset.currentPage = String(loadedPage);
        }

        loadingNext = false;
        rebuildFilters();
        refreshCardBadges();
        refreshFeatured();
        sortCards(sort?.value || 'relevance');
        updateInfiniteState();
        applyFilters();
        maybeLoadNearBottom();
      } catch (error) {
        console.warn('RADAR infinite scroll:', error);
        loadingNext = false;
        updateInfiniteState({ error: true });
        applyFilters();
      }
    }

    const maybeLoadNearBottom = () => {
      if (!nextUrl || loadingNext || !infinite) return;

      const viewportHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        0;

      const rect = infinite.getBoundingClientRect();

      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.offsetHeight
      );

      const currentBottom =
        (window.scrollY || window.pageYOffset || 0) +
        viewportHeight;

      const remainingDocument = documentHeight - currentBottom;

      if (
        rect.top <= viewportHeight + 1800 ||
        remainingDocument <= 1800
      ) {
        loadNextPage();
      }
    };

    section.addEventListener('click', (event) => {
      const viewButton = event.target.closest('[data-radar-view]');

      if (viewButton) {
        activeView = viewButton.dataset.radarView;

        section
          .querySelectorAll('[data-radar-view]')
          .forEach((button) => {
            button.classList.toggle(
              'is-active',
              button === viewButton
            );
          });

        if (sort) {
          sort.value = activeView === 'new'
            ? 'newest'
            : 'relevance';
        }

        sortCards(sort?.value || 'relevance');
        applyFilters();
        maybeLoadNearBottom();
        return;
      }

      const filterButton = event.target.closest('[data-radar-filter]');

      if (filterButton) {
        section
          .querySelectorAll('[data-radar-filter]')
          .forEach((item) => item.classList.remove('is-active'));

        filterButton.classList.add('is-active');

        activeFilter =
          filterButton.dataset.radarFilter === 'all'
            ? 'all'
            : `${filterButton.dataset.radarFilterType}:${filterButton.dataset.radarFilter}`;

        applyFilters();
        maybeLoadNearBottom();
        return;
      }

      if (event.target.closest('[data-radar-load-more]')) {
        loadNextPage();
      }
    });

    search?.addEventListener('input', () => {
      applyFilters();
      maybeLoadNearBottom();
    });

    sort?.addEventListener('change', () => {
      sortCards(sort.value);
      applyFilters();
    });

    smartViews
      ?.querySelectorAll('[data-radar-view]')
      .forEach((button) => {
        button.classList.toggle(
          'is-active',
          button.dataset.radarView === activeView
        );
      });

    rebuildFilters();
    refreshCardBadges();
    refreshFeatured();

    if (sort) {
      sort.value = activeView === 'new'
        ? 'newest'
        : 'relevance';
    }

    sortCards(sort?.value || 'relevance');
    applyFilters();
    updateInfiniteState();

    if (sentinel && nextUrl && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            loadNextPage();
          }
        },
        {
          root: null,
          rootMargin: '1800px 0px',
          threshold: 0
        }
      );

      observer.observe(sentinel);
    }

    window.addEventListener('scroll', maybeLoadNearBottom, {
      passive: true
    });

    document.addEventListener('scroll', maybeLoadNearBottom, {
      passive: true,
      capture: true
    });

    window.addEventListener('resize', maybeLoadNearBottom, {
      passive: true
    });

    window.addEventListener('orientationchange', maybeLoadNearBottom, {
      passive: true
    });

    document.addEventListener('touchend', maybeLoadNearBottom, {
      passive: true
    });

    if ('ResizeObserver' in window) {
      const sizeObserver = new ResizeObserver(maybeLoadNearBottom);
      sizeObserver.observe(grid);
      sizeObserver.observe(infinite);
    }

    window.setInterval(maybeLoadNearBottom, 1200);
    window.setTimeout(maybeLoadNearBottom, 120);
    window.setTimeout(maybeLoadNearBottom, 500);
    window.setTimeout(maybeLoadNearBottom, 1200);
  });
})();
